import { useMemo } from "react"
import { toast } from "react-toastify"

import { H1, H2 } from "~/ui-rtk/components/ui/typography"
import { Button } from "~/ui-rtk/components/ui/controls"
import { SOS_BOOK_A_CALL_URL } from "~/ui-rtk/constants/sos"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectUser } from "~/ui-rtk/app/selectors/user.selector"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  useAuthControllerLogoutMutation,
  useAuthControllerSwitchCompanyMutation,
} from "~/ui-rtk/api/authApi"
import { CompanyTier } from "~/ui-rtk/api/types/company.types"

/**
 * Whole-app paywall for STALE (aged-out non-paying) companies. Mounted from
 * the Auth routing INSTEAD of Main, so a single gate covers every route; the
 * API refuses these sessions anyway (403 COMPANY_STALE). Deliberately no
 * self-serve checkout (mirrors UpgradePage, APP-485): the paths out are a
 * booked call, a switch to a healthy company, or logout.
 */
export default function StalePaywall() {
  const user = useAppSelector(selectUser)
  const currentCompany = useCurrentCompany()
  const [logout, { isLoading: isLogoutLoading }] =
    useAuthControllerLogoutMutation()
  const [switchCompany, { isLoading: isSwitchLoading }] =
    useAuthControllerSwitchCompanyMutation()

  const otherCompanies = useMemo(
    () =>
      (user?.companies ?? []).filter(
        ({ company, companyId }) =>
          companyId !== currentCompany?.id &&
          company?.tier !== CompanyTier.STALE,
      ),
    [user, currentCompany],
  )

  const handleLogout = () => {
    void logout().finally(() => window.location.assign("/"))
  }

  const handleSwitch = async (companyId: string) => {
    // RTK Query mutations resolve to { data } | { error } — never reject.
    const result = await switchCompany({ switchCompanyDto: { companyId } })
    if ("error" in result) {
      toast.error("Failed to switch company", {
        toastId: "stale-paywall-switch-error",
      })
      return
    }
    window.location.reload()
  }

  return (
    <div className="w-full h-full overflow-y-auto flex items-center justify-center bg-basic-black text-basic-light">
      <div className="mx-auto max-w-160 px-4 py-10 flex flex-col gap-8 text-center">
        <div className="flex flex-col gap-4">
          <H1 className="font-semibold text-4xl">This account is inactive</H1>
          <p className="opacity-70">
            {currentCompany?.name ?? "This workspace"} has been inactive for a
            while, so dashboards and data access are paused. Book a call with
            our team to reactivate it.
          </p>
        </div>
        <div className="card bg-base-100 shadow-xl border border-basic-blue">
          <div className="card-body items-center text-center gap-2">
            <H2>Book a reactivation call</H2>
            <p className="opacity-70">
              30 minutes · with a founder, not a sales rep
            </p>
            <a
              href={SOS_BOOK_A_CALL_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary btn-wide mt-2"
            >
              Pick a time
            </a>
          </div>
        </div>
        {otherCompanies.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="opacity-70">Or continue with another company:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {otherCompanies.map(({ companyId, company }) => (
                <Button
                  key={companyId}
                  variant={{ variant: "outlined" }}
                  size="sm"
                  type="button"
                  isLoading={isSwitchLoading}
                  onClick={() => handleSwitch(companyId)}
                >
                  {company?.name ?? companyId}
                </Button>
              ))}
            </div>
          </div>
        )}
        <button
          type="button"
          className="link opacity-70 mx-auto"
          disabled={isLogoutLoading}
          onClick={handleLogout}
        >
          Log out
        </button>
      </div>
    </div>
  )
}
